import { z } from 'zod';

/**
 * Keyset pagination for every admin list. The client never sends an offset — it
 * sends the opaque `cursor` from the previous page's `nextCursor`. `total` is a
 * count **capped** server-side (so a table of millions never triggers a full
 * scan): a value at the cap means "this many or more".
 */
export const ADMIN_PAGE_MAX = 100;
export const ADMIN_TOTAL_CAP = 10_000;

export const AdminPageQuerySchema = z.object({
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(ADMIN_PAGE_MAX).default(25),
});
export type AdminPageQuery = z.infer<typeof AdminPageQuerySchema>;

/** Wrap an item schema into the standard `{ items, nextCursor, total }` page. */
export function adminPage<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    /** Pass back as `?cursor=` for the next page. `null` = last page. */
    nextCursor: z.string().nullable(),
    /** Capped at `ADMIN_TOTAL_CAP`; `null` when not counted. */
    total: z.number().int().nonnegative().nullable(),
  });
}

/** Render helper: `"10,000+"` at the cap, otherwise a plain localized count. */
export function formatAdminTotal(total: number | null): string | null {
  if (total == null) return null;
  return total >= ADMIN_TOTAL_CAP ? `${ADMIN_TOTAL_CAP.toLocaleString()}+` : total.toLocaleString();
}
